import * as React from 'react';
import S from 's-js';
import * as L from 'leaflet';
import { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { parseZoneCoordinates, LatLong, CircleCoords, PolygonCoords, ZONE_TYPE_CIRCLE, ZONE_TYPE_POLYGON } from 'data/models/ZoneCoordinates';
import { drawLocation, MAP_FILL_COLOR } from 'portcall/maps/maptools';
import { BerthProfile } from './model';
import 'leaflet-draw';
import 'leaflet-providers';

export class MapView extends SuperCriticalComponent<{ model : BerthProfile }> {
    ref : (el : HTMLDivElement | null) => void;

    constructor(props : { model : BerthProfile }) {
        super(props);
        this.ref = SuperCriticalReference(this, loadMap);
    }

    srender({ model: m } : { model : BerthProfile}) {
        return (
            <div className="berth-map" ref={this.ref}></div>
        );
    }
}

function loadMap({ model: m } : { model : BerthProfile }, el : HTMLDivElement) {
    const settings = m.DomainSettings.Map, map = L.map(el, { attributionControl: true });
    S.cleanup(() => map.remove());

    L.tileLayer((L.TileLayer as any).Provider.providers.Esri.url, (L.TileLayer as any).Provider.providers.Esri.variants.WorldImagery.options).addTo(map);

    const featureGroup = new L.FeatureGroup();
    featureGroup.addTo(map);

    let mapZoomInitialized = false;

    S(() => {
        const location = m.Berth() && m.Berth()!.Location();
        if (!location) return;

        S(() => {
            // draw berth zone
            const coords = location.Coords(),
                zoomCenter = drawLocation(featureGroup, coords);

            // TH 12/19/2016 -- B/c map coords are editable, avoid updating zoom after user edits map coords.
            if (!mapZoomInitialized) {
                if (zoomCenter) {
                    map.setView(zoomCenter, 16); // TH: Always zoom of 16?
                    mapZoomInitialized = true;
                } else {
                    // TH 12/19/2016 -- To avoid some UX jumpiness, don't call setView until we think we have map coords populated.
                    if (settings.Latitude() || settings.Longitude() || settings.Zoom()) {
                        map.setView([settings.Latitude(), settings.Longitude()], settings.Zoom());
                        mapZoomInitialized = true;
                    }
                }
            }
        });

        S(() => {
            const drawControl = new L.Control.Draw({
                draw: {
                    //restrict drawing to polygon and circle
                    polyline: false,
                    marker: false,
                    rectangle: false,
                    polygon: {
                        shapeOptions: {
                            color: MAP_FILL_COLOR,
                            fillColor: MAP_FILL_COLOR,
                            weight: 2
                        }
                    },
                    circle: {
                        shapeOptions: {
                            color: MAP_FILL_COLOR,
                            fillColor: MAP_FILL_COLOR,
                            weight: 2
                        }
                    }
                },
                edit: {
                    featureGroup: featureGroup
                }
            });

            if (m.Model.Ref.CanEdit()) {
                // TH: Hack leaflet draw private state to customize toolbar button text. There should be a better way...
                const leafletDrawButtons = (L as any).drawLocal.draw.toolbar.buttons,
                    oldPolygonText = leafletDrawButtons.polygon,
                    oldCircleText = leafletDrawButtons.circle;
                leafletDrawButtons.polygon = 'Outline the berth';
                leafletDrawButtons.circle = 'Add an anchorage';
                map.addControl(drawControl);
                S.cleanup(() => {
                    map.removeControl(drawControl)
                    // If set, ZoneOperationsParams references the drawControl.
                    // So clear ZoneOperationsParams if drawControl is getting destroyed.
                    location!.ZoneOperationsParams(null);
                });

                // TH: Now that we've added the drawControl, we can reset our button text hack...
                leafletDrawButtons.polygon = oldPolygonText;
                leafletDrawButtons.circle = oldCircleText;

                // aah: the event handlers below all have the correct, more specific DrawEvent signature,
                // but the base typedefs for leaflet expect just plain LeafletEventHandlerFn's
                // so have to downcast to satisfy TS callback contravariance constraint
                map.on('draw:drawstart', ZoneStarted as L.LeafletEventHandlerFn);
                map.on('draw:created', ZoneCreated as L.LeafletEventHandlerFn);
                map.on('draw:edited', ZoneEdited as L.LeafletEventHandlerFn);
                map.on('draw:deleted', ZoneDeleted as L.LeafletEventHandlerFn);
                S.cleanup(() => {
                    map.off('draw:drawstart', ZoneStarted as L.LeafletEventHandlerFn);
                    map.off('draw:created', ZoneCreated as L.LeafletEventHandlerFn);
                    map.off('draw:edited', ZoneEdited as L.LeafletEventHandlerFn);
                    map.off('draw:deleted', ZoneDeleted as L.LeafletEventHandlerFn);
                });
            }

            function ZoneStarted(e : L.DrawEvents.DrawStart) {
                if (e.layerType !== 'polygon' && e.layerType !== 'circle') throw new Error('Unknown layer type: ' + e.layerType);
                const layerType = e.layerType as 'polygon' | 'circle';

                if (S.sample(location!.Model.ZoneCoordinates) || layerType === 'circle') {
                    location!.ZoneOperationsParams({
                        selectedZoneType: layerType,
                        // TH: Hmmm... Hack private leaflet state to cancel edit action.
                        cancelLeafletEdit: () => (drawControl as any)._toolbars[(L as any).DrawToolbar.TYPE].disable()
                    });
                }
            }

            function ZoneCreated(e : L.DrawEvents.Created) {
                updateZone(e.layer);
            }

            function ZoneEdited(e : L.DrawEvents.Edited) {
                const layer = e.layers.getLayers()[0];
                if (!layer) return;
                updateZone(layer);
            }

            function updateZone(layer : L.Layer) {
                S.freeze(() => {
                    if (layer instanceof L.Circle) {
                        location!.Model.ZoneType(ZONE_TYPE_CIRCLE);
                        location!.Model.ZoneCoordinates(CircleCoords.fromLeaflet((layer as L.Circle)).toString());
                    } else if (layer instanceof L.Polygon) {
                        location!.Model.ZoneType(ZONE_TYPE_POLYGON);
                        location!.Model.ZoneCoordinates(PolygonCoords.fromLeaflet((layer as L.Polygon)).toString());
                    }
                });
            }

            function ZoneDeleted(e : L.DrawEvents.Deleted) {
                if (!e.layers.getLayers().length) return;
                S.freeze(() => {
                    location!.Model.ZoneCoordinates(null);
                    location!.Model.ZoneType(null);
                });
            }
        });
    });
}


// WEBPACK FOOTER //
// ./apps/portcall/berthprofile/mapview.tsx